import * as React from "react";
import { PropsWithChildren } from "react";
import { useLocalStorage } from "../hooks";
import { ETLCodes } from "../locales";

interface ActionDetail {
  route: string;
  tlCode?: ETLCodes;
  name?: string;
}

interface IActionHistoryContext extends React.PropsWithChildren {
  actionHistory: Array<ActionDetail>;
  addToActionHistory?: (route: ActionDetail) => void;
}

const ActionHistoryContext = React.createContext<IActionHistoryContext>(null);

interface IDetailHistoryProviderProps extends PropsWithChildren {}

const LOCALSTORAGE_ACTION_HISTORY = "ACTIONS_HISTORY";
const MAX_ITEMS_IN_HISTORY = 15;

export const ActionHistoryProvider: React.FunctionComponent<IDetailHistoryProviderProps> = ({ children }) => {
  const [actionHistory, setActionHistory] = useLocalStorage<Array<ActionDetail>>(LOCALSTORAGE_ACTION_HISTORY, []);

  const addToActionHistory = React.useCallback(
    (actionDetail: ActionDetail) => {
      const toDelete = actionHistory.findIndex((c) => c.name === actionDetail.name);
      if (toDelete && toDelete >= 0) {
        actionHistory.splice(toDelete, 1);
      }
      const newArray = [actionDetail, ...actionHistory];
      const nextHistory = newArray
        .filter((item, pos) => newArray.map((c) => c.name).indexOf(item.name) === pos)
        .slice(0, MAX_ITEMS_IN_HISTORY);
      if (JSON.stringify(nextHistory) !== JSON.stringify(actionHistory) && actionDetail.name) {
        setActionHistory(nextHistory);
      }
    },
    [setActionHistory, actionHistory],
  );

  return (
    <ActionHistoryContext.Provider value={{ actionHistory, addToActionHistory }}>
      {children}
    </ActionHistoryContext.Provider>
  );
};

export const useDetailHistoryContext = () => React.useContext(ActionHistoryContext);
